// ===================================================================
// 📊 PAINEL DE CONTROLE - Admin e Analista
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { DimensioningStudy } from '../../components/DimensioningStudy';
import { getSelectionModeLabel, isOrdemInscricaoMode as checkOrdemMode, isProvaMode as checkProvaMode } from '../../utils/selectionMode';

const numStyle = { fontSize: '11px', color: '#4B5563' };
const totalNumStyle = { fontSize: '14px', fontWeight: '700', color: '#374151' };
const groupBorder = { borderLeft: '3px solid #374151' };

export const PainelControle = React.memo(({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [processSummary, setProcessSummary] = useState([]);
  const [assignmentSummary, setAssignmentSummary] = useState(null);
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);

  const selectedProcessMeta = useMemo(
    () => processes.find((p) => String(p.id) === String(selectedProcessId)),
    [processes, selectedProcessId]
  );
  const processIsProva = checkProvaMode(selectedProcessMeta?.selection_mode);
  const processIsOrdem = checkOrdemMode(selectedProcessMeta?.selection_mode);
  const analysisColumnLabel = processIsProva
    ? 'ANÁLISE DOS PEDIDOS DE ATENDIMENTO ESPECIAL'
    : processIsOrdem
      ? 'ANÁLISE DE INSCRIÇÕES'
      : 'ANÁLISE CURRICULAR';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getControlPanel(selectedProcessId ? parseInt(selectedProcessId, 10) : undefined);
      setProcessSummary(data.process_summary || []);
      setAssignmentSummary(data.assignment_summary || null);
      setProcess(data.process || null);
    } catch (err) {
      setProcessSummary([]);
      setAssignmentSummary(null);
      setProcess(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProcessId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading && processes.length === 0) {
    return <LoadingSpinner text="Carregando painel..." />;
  }

  return (
    <div style={{ ...styles.contentContainer, padding: '12px 24px' }}>
      <h2 style={{ ...styles.cardTitle, marginBottom: '4px', fontSize: '18px' }}>📊 Painel de Controle</h2>
      <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
        Visão consolidada do processo seletivo e atribuições
      </p>

      <div style={{ ...styles.card, marginBottom: '12px', padding: '12px' }}>
        <label style={{ ...styles.label, fontSize: '12px' }}>Processo seletivo:</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
          <select
            value={selectedProcessId}
            onChange={setSelectedProcessId}
            style={{ ...styles.select, maxWidth: '400px' }}
          >
            <option value="">Selecione o processo</option>
            {processes.map((p) => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
          {selectedProcessMeta && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '999px',
                backgroundColor: processIsProva ? '#FEF3C7' : processIsOrdem ? '#ECFDF5' : '#EEF2FF',
                color: processIsProva ? '#92400E' : processIsOrdem ? '#047857' : '#3730A3',
                border: `1px solid ${processIsProva ? '#FCD34D' : processIsOrdem ? '#6EE7B7' : '#C7D2FE'}`,
              }}
            >
              Modalidade: {getSelectionModeLabel(selectedProcessMeta.selection_mode).toLowerCase()}
            </span>
          )}
        </div>
        {processIsProva && (
          <p style={{ marginTop: '10px', fontSize: '12px', color: '#92400E', lineHeight: 1.5, maxWidth: '820px' }}>
            <strong>Modo prova:</strong> o painel consolida <strong>inscritos</strong>, a coluna{' '}
            <strong>análise dos pedidos de atendimento especial</strong> (deferidos, indeferidos e pendentes por curso)
            e <strong>pagamentos</strong>. Em <strong>Controle de atribuição</strong>, a mesma lógica aparece ao lado de
            pedido de isenção e recursos. Indicadores adicionais da prova (por exemplo, logística de aplicação) podem ser
            ampliados nas próximas entregas. O <strong>estudo de dimensionamento curricular</strong> não se aplica a este
            modo.
          </p>
        )}
        {!selectedProcessId && processes.length > 0 && (
          <p style={{ marginTop: 12, fontSize: 14, color: '#6B7280', fontStyle: 'italic' }}>
            👋 Selecione o processo seletivo com o qual deseja trabalhar para visualizar os dados e acessar as demais funcionalidades.
          </p>
        )}
      </div>

      {loading ? (
        <LoadingSpinner text="Atualizando..." />
      ) : (
        <>
          {/* Tabela do processo por curso */}
          <div style={{ ...styles.card, marginBottom: '12px', padding: '12px' }}>
            <h3 style={{ ...styles.cardTitle, marginBottom: '8px', fontSize: '14px' }}>Processo seletivo por curso</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #E5E7EB', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={thStyle} rowSpan={3}>Curso</th>
                    <th style={thStyle} rowSpan={3}>Unidade</th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={3}>INSCRITOS</th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={7}>
                      {analysisColumnLabel}
                    </th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={4}>PAGAMENTOS</th>
                  </tr>
                  <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={{ ...thStyle, ...groupBorder }} rowSpan={2}>Vagas</th>
                    <th style={thStyle} rowSpan={2}>QTD</th>
                    <th style={thStyle} rowSpan={2}>Cand./vaga</th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={2}>Deferidos</th>
                    <th style={thStyle} colSpan={2}>Indeferidos</th>
                    <th style={thStyle} colSpan={2}>Pendentes</th>
                    <th style={thStyle} rowSpan={2}>Total</th>
                    <th style={{ ...thStyle, ...groupBorder }} rowSpan={2}>Pagos</th>
                    <th style={thStyle} rowSpan={2}>Isentos</th>
                    <th style={thStyle} rowSpan={2}>Indeferidos</th>
                    <th style={thStyle} rowSpan={2}>Total</th>
                  </tr>
                  <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ ...thStyle, ...groupBorder }}>QTD</th>
                    <th style={thStyle}>%</th>
                    <th style={thStyle}>QTD</th>
                    <th style={thStyle}>%</th>
                    <th style={thStyle}>QTD</th>
                    <th style={thStyle}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {processSummary.length === 0 ? (
                    <tr><td colSpan={16} style={{ padding: '12px', textAlign: 'center', color: '#6B7280', fontSize: '11px' }}>Nenhum dado para exibir</td></tr>
                  ) : (
                    <>
                      {processSummary.map((row, i) => (
                        <tr
                          key={row.course_id}
                          style={{
                            borderBottom: '1px solid #E5E7EB',
                            backgroundColor: hoveredRow === `course-${i}` ? '#EFF6FF' : (i % 2 === 0 ? '#FFFFFF' : '#F9FAFB'),
                            transition: 'background-color 0.15s ease',
                          }}
                          onMouseEnter={() => setHoveredRow(`course-${i}`)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          <td style={tdStyleLeft}>{row.course_name}</td>
                          <td style={tdStyle}>{row.course_campus || '—'}</td>
                          <td style={{ ...tdStyle, ...groupBorder }}>{row.vacancies}</td>
                          <td style={tdStyle}>{row.inscritos}</td>
                          <td style={tdStyle}>{(() => {
                            const v = row.vacancies ?? 0;
                            const q = row.inscritos ?? 0;
                            const cv = v > 0 ? q / v : null;
                            const color = cv === null ? '#6B7280' : cv <= 1 ? '#DC2626' : cv <= 1.5 ? '#D97706' : '#059669';
                            return <span style={{ ...totalNumStyle, color }}>{cv !== null ? cv.toFixed(2).replace('.', ',') : '—'}</span>;
                          })()}</td>
                          <td style={{ ...tdStyle, ...groupBorder }}><span style={numStyle}>{row.deferidos}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{typeof row.pct_deferidos === 'number' ? row.pct_deferidos.toFixed(2).replace('.', ',') : row.pct_deferidos}%</span></td>
                          <td style={tdStyle}><span style={numStyle}>{row.indeferidos}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{typeof row.pct_indeferidos === 'number' ? row.pct_indeferidos.toFixed(2).replace('.', ',') : row.pct_indeferidos}%</span></td>
                          <td style={tdStyle}><span style={numStyle}>{row.pendentes ?? (row.inscritos - (row.deferidos ?? 0) - (row.indeferidos ?? 0))}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{typeof row.pct_pendentes === 'number' ? row.pct_pendentes.toFixed(2).replace('.', ',') : '—'}%</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{row.total_analise ?? (row.deferidos + row.indeferidos + (row.pendentes ?? 0))}</span></td>
                          <td style={{ ...tdStyle, ...groupBorder }}><span style={numStyle}>{row.pagos}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{row.isentos}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{row.isentos_indef}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{row.total_pagos ?? (row.pagos + row.isentos + row.isentos_indef)}</span></td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#E5E7EB', borderTop: '2px solid #9CA3AF' }}>
                        <td colSpan={2} style={{ ...tdStyleLeft, fontWeight: '700', padding: '8px 6px' }}>Total</td>
                        <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.vacancies ?? 0), 0)}</span></td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.inscritos ?? 0), 0)}</span></td>
                        <td style={tdStyle}>{(() => {
                          const sumV = processSummary.reduce((s, r) => s + (r.vacancies ?? 0), 0);
                          const sumQ = processSummary.reduce((s, r) => s + (r.inscritos ?? 0), 0);
                          const cv = sumV > 0 ? sumQ / sumV : null;
                          const color = cv === null ? '#6B7280' : cv <= 1 ? '#DC2626' : cv <= 1.5 ? '#D97706' : '#059669';
                          return <span style={{ ...totalNumStyle, color }}>{cv !== null ? cv.toFixed(2).replace('.', ',') : '—'}</span>;
                        })()}</td>
                        <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.deferidos ?? 0), 0)}</span></td>
                        <td style={tdStyle}>—</td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.indeferidos ?? 0), 0)}</span></td>
                        <td style={tdStyle}>—</td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.pendentes ?? 0), 0)}</span></td>
                        <td style={tdStyle}>—</td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.total_analise ?? (r.deferidos + r.indeferidos + (r.pendentes ?? 0))), 0)}</span></td>
                        <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.pagos ?? 0), 0)}</span></td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.isentos ?? 0), 0)}</span></td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.isentos_indef ?? 0), 0)}</span></td>
                        <td style={tdStyle}><span style={totalNumStyle}>{processSummary.reduce((s, r) => s + (r.total_pagos ?? (r.pagos + r.isentos + r.isentos_indef)), 0)}</span></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabela de atribuição por analista */}
          <div style={{ ...styles.card, padding: '12px' }}>
            <h3 style={{ ...styles.cardTitle, marginBottom: '6px', fontSize: '14px' }}>Controle de atribuição</h3>
            <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '8px' }}>
              Visão por analista para orientar a distribuição.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #E5E7EB', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={thStyle} rowSpan={2}>Analista</th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={4}>Pedido de isenção</th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={4}>
                      {processIsProva ? 'ANÁLISE DOS PEDIDOS DE ATENDIMENTO ESPECIAL' : processIsOrdem ? 'Análise de inscrições' : 'Análise curricular'}
                    </th>
                    <th style={{ ...thStyle, ...groupBorder }} colSpan={4}>Recursos</th>
                    <th style={{ ...thStyle, ...groupBorder }} rowSpan={2} title="Participação no total de análises executadas (isenção + curricular + recursos)">% total</th>
                    <th style={{ ...thStyle, ...groupBorder }} rowSpan={2} title="Total de análises feitas ou a fazer (soma dos totais de cada seção)">Total</th>
                  </tr>
                  <tr style={{ backgroundColor: '#F3F4F6', borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ ...thStyle, ...groupBorder }}>Deferidas</th>
                    <th style={thStyle}>Indeferidas</th>
                    <th style={thStyle}>Pendentes</th>
                    <th style={thStyle}>Total</th>
                    <th style={{ ...thStyle, ...groupBorder }}>Deferidas</th>
                    <th style={thStyle}>Indeferidas</th>
                    <th style={thStyle}>Pendentes</th>
                    <th style={thStyle}>Total</th>
                    <th style={{ ...thStyle, ...groupBorder }}>Deferidas</th>
                    <th style={thStyle}>Indeferidas</th>
                    <th style={thStyle}>Pendentes</th>
                    <th style={thStyle}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {!assignmentSummary?.by_analyst?.length ? (
                    <tr><td colSpan={15} style={{ padding: '12px', textAlign: 'center', color: '#6B7280', fontSize: '11px' }}>Nenhum dado para exibir</td></tr>
                  ) : (() => {
                    const rows = assignmentSummary.by_analyst;
                    const totalExec = rows.reduce((s, r) => {
                      const ex = (r.exemption?.deferidas ?? 0) + (r.exemption?.indeferidas ?? 0);
                      const cur = (r.curricular?.deferidas ?? 0) + (r.curricular?.indeferidas ?? 0);
                      const ap = (r.appeal?.deferidas ?? 0) + (r.appeal?.indeferidas ?? 0);
                      return s + ex + cur + ap;
                    }, 0);
                    const rawPcts = totalExec > 0 ? rows.map((row) => {
                      const exExec = (row.exemption?.deferidas ?? 0) + (row.exemption?.indeferidas ?? 0);
                      const curExec = (row.curricular?.deferidas ?? 0) + (row.curricular?.indeferidas ?? 0);
                      const apExec = (row.appeal?.deferidas ?? 0) + (row.appeal?.indeferidas ?? 0);
                      return ((exExec + curExec + apExec) / totalExec) * 100;
                    }) : [];
                    const rounded = rawPcts.map(r => Math.floor(r * 100) / 100);
                    const sumRounded = rounded.reduce((a, b) => a + b, 0);
                    const diff = totalExec > 0 ? Math.round((100 - sumRounded) * 100) / 100 : 0;
                    const maxIdx = rawPcts.length ? rawPcts.reduce((best, v, i) => (v > rawPcts[best] ? i : best), 0) : 0;
                    const pcts = rounded.map((p, i) => (i === maxIdx ? p + diff : p));
                    const sorted = [...new Set(pcts)].sort((a, b) => a - b);
                    const minVal = sorted[0];
                    const secondMinVal = sorted[1];
                    const maxVal = sorted[sorted.length - 1];
                    const getPctColor = (val) => {
                      if (val === minVal) return '#DC2626';
                      if (sorted.length >= 2 && val === secondMinVal) return '#D97706';
                      if (val === maxVal) return '#059669';
                      return '#6B7280';
                    };
                    const sumExDef = rows.reduce((s, r) => s + (r.exemption?.deferidas ?? 0), 0);
                    const sumExIndef = rows.reduce((s, r) => s + (r.exemption?.indeferidas ?? 0), 0);
                    const sumExPend = rows.reduce((s, r) => s + (r.exemption?.pendentes ?? 0), 0);
                    const sumExTotal = rows.reduce((s, r) => s + (r.exemption?.total ?? 0), 0);
                    const sumCurDef = rows.reduce((s, r) => s + (r.curricular?.deferidas ?? 0), 0);
                    const sumCurIndef = rows.reduce((s, r) => s + (r.curricular?.indeferidas ?? 0), 0);
                    const sumCurPend = rows.reduce((s, r) => s + (r.curricular?.pendentes ?? 0), 0);
                    const sumCurTotal = rows.reduce((s, r) => s + (r.curricular?.total ?? 0), 0);
                    const sumApDef = rows.reduce((s, r) => s + (r.appeal?.deferidas ?? 0), 0);
                    const sumApIndef = rows.reduce((s, r) => s + (r.appeal?.indeferidas ?? 0), 0);
                    const sumApPend = rows.reduce((s, r) => s + (r.appeal?.pendentes ?? 0), 0);
                    const sumApTotal = rows.reduce((s, r) => s + (r.appeal?.total ?? 0), 0);
                    return <>
                    {rows.map((row, i) => {
                      const pct = pcts[i] ?? 0;
                      const pctStr = totalExec > 0 ? pct.toFixed(2).replace('.', ',') : '—';
                      const color = getPctColor(pct);
                      const exTotal = row.exemption?.total ?? 0;
                      const exDef = row.exemption?.deferidas ?? 0;
                      const exIndef = row.exemption?.indeferidas ?? 0;
                      const exPend = row.exemption?.pendentes ?? 0;
                      const curTotal = row.curricular?.total ?? 0;
                      const curDef = row.curricular?.deferidas ?? 0;
                      const curIndef = row.curricular?.indeferidas ?? 0;
                      const curPend = row.curricular?.pendentes ?? 0;
                      const apTotal = row.appeal?.total ?? 0;
                      const apDef = row.appeal?.deferidas ?? 0;
                      const apIndef = row.appeal?.indeferidas ?? 0;
                      const apPend = row.appeal?.pendentes ?? 0;
                      const rowKey = `assign-${row.analyst_id ?? 'na'}-${i}`;
                      return (
                        <tr
                          key={rowKey}
                          style={{
                            borderBottom: '1px solid #E5E7EB',
                            backgroundColor: hoveredRow === rowKey ? '#EFF6FF' : (i % 2 === 0 ? '#FFFFFF' : '#F9FAFB'),
                            transition: 'background-color 0.15s ease',
                          }}
                          onMouseEnter={() => setHoveredRow(rowKey)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          <td style={{ ...tdStyleLeft, fontWeight: row.analyst_id ? '500' : '600', fontStyle: row.analyst_id ? 'normal' : 'italic' }}>
                            {row.analyst_name}
                          </td>
                          <td style={{ ...tdStyle, ...groupBorder }}><span style={numStyle}>{exDef}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{exIndef}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{exPend}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{exTotal}</span></td>
                          <td style={{ ...tdStyle, ...groupBorder }}><span style={numStyle}>{curDef}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{curIndef}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{curPend}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{curTotal}</span></td>
                          <td style={{ ...tdStyle, ...groupBorder }}><span style={numStyle}>{apDef}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{apIndef}</span></td>
                          <td style={tdStyle}><span style={numStyle}>{apPend}</span></td>
                          <td style={tdStyle}><span style={totalNumStyle}>{apTotal}</span></td>
                          <td style={{ ...tdStyle, ...groupBorder, color }} title="Participação no total de análises executadas">
                            <span style={{ ...totalNumStyle, color }}>{totalExec > 0 ? `${pctStr}%` : '—'}</span>
                          </td>
                          <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{exTotal + curTotal + apTotal}</span></td>
                        </tr>
                      );
                    })}
                    <tr style={{ backgroundColor: '#E5E7EB', borderTop: '2px solid #9CA3AF' }}>
                      <td style={{ ...tdStyleLeft, fontWeight: '700', padding: '8px 6px' }}>Total</td>
                      <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{sumExDef}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumExIndef}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumExPend}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumExTotal}</span></td>
                      <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{sumCurDef}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumCurIndef}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumCurPend}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumCurTotal}</span></td>
                      <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{sumApDef}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumApIndef}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumApPend}</span></td>
                      <td style={tdStyle}><span style={totalNumStyle}>{sumApTotal}</span></td>
                      <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>100%</span></td>
                      <td style={{ ...tdStyle, ...groupBorder }}><span style={totalNumStyle}>{sumExTotal + sumCurTotal + sumApTotal}</span></td>
                    </tr>
                    </>;
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {!processIsProva && !processIsOrdem && <DimensioningStudy process={process} assignmentSummary={assignmentSummary} />}
        </>
      )}
    </div>
  );
});

const thStyle = { padding: '6px 4px', textAlign: 'center', fontWeight: '600', color: '#374151', borderRight: '1px solid #D1D5DB', fontSize: '11px' };
const tdStyle = { padding: '6px 4px', color: '#4B5563', borderRight: '1px solid #E5E7EB', textAlign: 'center', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' };
const tdStyleLeft = { padding: '6px 4px', color: '#4B5563', borderRight: '1px solid #E5E7EB', textAlign: 'left', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' };
